import { useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button, Dialog } from '@fluent/ui';
import { videoPosterUrl, videoUrl } from '../video/videoUrl.js';
import './GrammarVideo.css';

export interface GrammarVideoProps {
  /** The tip's `video_key`, e.g. `grammar/now-vs-usually-v1`. */
  videoKey: string;
  /** Read by screen readers as the player's name (the tip's title). */
  title?: string;
}

/**
 * GrammarVideo — the ▶ button on a grammar tip, and the full-screen player it
 * opens (≤30 s motion-graphic explainer with Georgian captions, so it also
 * works muted). Added 2026-10-08 with the first video (now vs usually).
 *
 * Playback starts inside the tap: iPhone Safari plays with sound only when
 * play() runs in the user's gesture, so the dialog mounts under flushSync and
 * the video's ref calls play() before the click handler returns. If a browser
 * still refuses, the native ▶ control is right there.
 */
export function GrammarVideo({ videoKey, title }: GrammarVideoProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const label = t('lesson.grammar_tip.video');

  return (
    <>
      <Button
        variant="secondary"
        onClick={() => flushSync(() => setOpen(true))}
        leadingIcon={<PlayIcon />}
        data-testid="grammar-video-btn"
      >
        {label}
      </Button>
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={title ?? label}
        description={label}
        className="fluent-grammarvideo"
      >
        <video
          ref={startPlayback}
          className="fluent-grammarvideo__video"
          src={videoUrl(videoKey)}
          poster={videoPosterUrl(videoKey)}
          controls
          playsInline
          preload="auto"
          data-testid="grammar-video"
        />
      </Dialog>
    </>
  );
}

function startPlayback(el: HTMLVideoElement | null) {
  if (!el) return;
  // Older browsers return undefined instead of a promise; a refusal just leaves the ▶ control.
  void el.play()?.catch(() => undefined);
}

function PlayIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      <path d="M8 5.5v13a1 1 0 0 0 1.5.86l10.4-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5z" fill="currentColor" />
    </svg>
  );
}
